import { Injectable } from "@angular/core";
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";

import { BehaviorSubject, EMPTY, Observable, throwError } from "rxjs";
import { MatSnackBar } from "@angular/material/snack-bar";
import { catchError, filter, finalize, switchMap, take } from "rxjs/operators";

import { LoginService } from "./core/services/auth/login/login.service";
import { RefreshService } from "./core/services/auth/refresh/refresh.service";
import { FuseLoadingService } from "@fuse/services/loading";
import { FuseAlertService } from "@fuse/components/alert";
import { environment } from "environments/environment";

@Injectable()
export class AppInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);

  constructor(
    private loginService: LoginService,
    private refreshService: RefreshService,
    private snackBar: MatSnackBar,
    private _fuseLoadingService: FuseLoadingService,
    private _fuseAlertService: FuseAlertService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    this.showLoadingBar();

    // Skip adding auth header for auth endpoints that don't need it
    if (this.shouldAddAuthHeader(request)) {
      request = this.addAuthHeader(request);
    }

    return next.handle(request).pipe(
      // No retry(1) — retrying with the same expired token causes 428
      catchError((error) => {
        // If the refresh endpoint itself fails, log out immediately
        if (request.url.includes("/refresh")) {
          this.loginService.logOutWrapper();
          return EMPTY;
        }

        // If the logout endpoint fails, still clean up locally
        if (request.url.includes("/logout")) {
          this.loginService.logOutWrapper();
          return EMPTY;
        }

        return this.handleResponseError(error, request, next);
      }),
      finalize(() => this.hideLoadingBar())
    ) as Observable<HttpEvent<any>>;
  }

  /**
   * Determine if this request should carry an Authorization header.
   * Excludes auth endpoints (login, register, refresh, forgot-password)
   * to prevent sending expired JWTs on token refresh requests.
   */
  private shouldAddAuthHeader(request: HttpRequest<any>): boolean {
    if (!request.url.includes("gravityfusion.com.au")) {
      return false;
    }
    // Never attach the (potentially expired) JWT to the refresh endpoint.
    // The refresh endpoint authenticates via the refresh token in the body.
    if (request.url.includes("/refresh")) {
      return false;
    }
    // Don't attach to login or registration endpoints either
    if (
      request.url.includes("/login") ||
      request.url.includes("/register") ||
      request.url.includes("/verify-email") ||
      request.url.includes("/forgot-password") ||
      request.url.includes("/user/contact/verify")
    ) {
      return false;
    }
    return true;
  }

  addAuthHeader(request: HttpRequest<any>): HttpRequest<any> {
    const authHeader = localStorage.getItem("token");
    if (authHeader) {
      return request.clone({
        setHeaders: {
          Authorization: authHeader,
        },
      });
    }
    return request;
  }

  /**
   * Refresh the access token using the BehaviorSubject pattern.
   * - If no refresh is in progress, initiates one and signals waiting requests.
   * - If a refresh IS in progress, queues the caller until the new token arrives.
   */
  private refreshToken(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.refreshService.refreshWithRefreshToken().pipe(
        switchMap(() => {
          this.isRefreshing = false;
          const newToken = localStorage.getItem("token");
          this.refreshTokenSubject.next(newToken);
          // Retry the original request with the fresh token
          return next.handle(this.addAuthHeader(request));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          this.logout();
          return EMPTY;
        })
      );
    } else {
      // Another request already triggered the refresh — wait for it
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap(() => {
          // Retry with the new token that was just stored
          return next.handle(this.addAuthHeader(request));
        })
      );
    }
  }

  logout() {
    this.loginService.signOut();
  }

  handleResponseError(
    error: any,
    request?: HttpRequest<any>,
    next?: HttpHandler
  ): Observable<any> {
    if (error.error instanceof ErrorEvent) {
      // client-side error
      return throwError(() => new Error(error));
    } else {
      // server-side error
      if (error.status === 0) {
        return throwError(
          () => new Error("Error connecting to server, try again later.")
        );
      } else {
        if (error.status === 406) {
          if (
            typeof error.error === "string" &&
            error.error.includes("[Token is expired]")
          ) {
            // Token expired — attempt a refresh and retry the request
            localStorage.removeItem("token");
            return this.refreshToken(request, next);
          } else {
            // Not Acceptable for a reason other than expiry (e.g. invalid signature)
            this.logout();
            return EMPTY;
          }
        } else if (error.status === 428) {
          // 428 = No Token Used / Token not recognized.
          // Instead of immediately logging out, try to refresh first.
          // This handles the case where the token was cleared or expired
          // between the request being sent and the response arriving.
          const refreshToken = localStorage.getItem("refreshToken");
          if (refreshToken && request && next) {
            localStorage.removeItem("token");
            return this.refreshToken(request, next);
          }
          // No refresh token available — force logout
          this.showSnackBar(error.error);
          this.loginService.logOutWrapper();
          return EMPTY;
        } else if (error.status === 412) {
          // user not found
          this.showSnackBar(error.error);
          if (
            error.url.includes(
              `${environment.apiUrlCompliance}/security/nsw`
            ) ||
            error.url.includes(`${environment.apiUrlCompliance}/liquor/nsw`)
          ) {
            return throwError(() => error);
          } else {
            return EMPTY;
          }
        } else if (error.status === 403) {
          // status forbidden — authenticated, but not permitted to do this.
          // TEZY2-4388 — a 403 must NOT log the user out (that's 401's job);
          // surface the message and keep them on the page.
          this.showSnackBar(error.error);
          return EMPTY;
        } else if (error.status === 401) {
          // 401 = Token is invalid/expired/tampered.
          // Try a refresh before giving up — the refresh token (30-day TTL)
          // may still be valid even if the access token has expired.
          if (request?.responseType === "blob") {
            return EMPTY;
          }
          const refreshToken = localStorage.getItem("refreshToken");
          if (refreshToken && request && next) {
            localStorage.removeItem("token");
            return this.refreshToken(request, next);
          }
          // No refresh token — force logout
          this.showSnackBar(error.error);
          this.logout();
          return EMPTY;
        } else if (error.status === 502) {
          // Bad Gateway
          this.showSnackBar(error.error);
          this.logout();
          return EMPTY;
        }

        return throwError(() => error);
      }
    }
  }

  showSnackBar(text) {
    this.snackBar.open(`${text}`, "X", {
      duration: 2000,
      verticalPosition: "top",
      horizontalPosition: "center",
    });
  }

  /**
   * Show the loading bar
   */
  showLoadingBar(): void {
    this._fuseLoadingService.show();
  }

  /**
   * Hide the loading bar
   */
  hideLoadingBar(): void {
    this._fuseLoadingService.hide();
  }
}
